<template>
    <Banner
        v-if="impersonating"
        iconName="alert"
        title="WARNING!"
        message="Impersonating user."
    />

    <div class="grid min-h-screen grid-cols-12 content-start">
        <div
            class="hidden md:block col-span-12 md:col-span-2 bg-white min-h-screen h-full"
        >
            <div class="h-full border-r border-stone-200 bg-white">
                <AppSidebar
                    :appName="appName"
                    :currentRoute="currentRoute"
                    :authUser="authUser"
                    :authCompanyUser="authCompanyUser"
                    :integrationsRequiringAttention="
                        integrationsRequiringAttention
                    "
                />
            </div>
        </div>
        <div class="block md:hidden col-span-12 md:col-span-2 bg-white">
            <AppMobileTopbar
                :appName="appName"
                :currentRoute="currentRoute"
                :authUser="authUser"
                :authCompanyUser="authCompanyUser"
                :integrationsRequiringAttention="integrationsRequiringAttention"
            />
        </div>
        <div class="col-span-12 md:col-span-10">
            <PageWrapper
                :pageMeta="pageMeta"
                :authUser="authUser"
                :authCompanyUser="authCompanyUser"
                :impersonating="impersonating"
            >
                <template #left>
                    <slot name="left" />
                </template>
                <template #right>
                    <slot name="right" />
                </template>
                <slot />
            </PageWrapper>
        </div>

        <FlashMessage />
    </div>
</template>

<script setup lang="ts">
    import { onBeforeMount, onMounted } from 'vue'
    import useSupportChat, {
        type TawktoSupportChatInput,
    } from '@/Shared/Hook/useSupportChat'
    import AppSidebar from '@/Shared/Layout/App/Sidebar/AppSidebar.vue'
    import AppMobileTopbar from '@/Shared/Layout/App/Sidebar/AppMobileTopbar.vue'
    import PageWrapper from '@/Shared/Layout/App/Sidebar/PageWrapper.vue'
    import FlashMessage from '@/Shared/Component/FlashMessage.vue'
    import Banner from '@/Shared/Component/Banner.vue'
    import type { FlashStatusType, PageMeta } from '@/Shared/AutoGenerated/Main'
    import User from '@/App/User/Entity/User'
    import CompanyUser from '@/App/CompanyUser/Entity/CompanyUser'
    import useFlash from '@/Shared/Hook/useFlash'
    import useHeadwayWidget from '@/Shared/Hook/useHeadwayWidget'

    interface AppLayoutProps {
        pageMeta: PageMeta | null
        appName: string
        authUser: User
        authCompanyUser: CompanyUser
        impersonating: boolean
        currentRoute: string
        tawktoScriptInputs: TawktoSupportChatInput
        flashStatus: FlashStatusType | null
        integrationsRequiringAttention: number
    }

    const props = defineProps<AppLayoutProps>()

    onBeforeMount(() => {
        if (!props.flashStatus) {
            return
        }

        useFlash().display(props.flashStatus)
    })

    onMounted(() => {
        useSupportChat().injectTawktoScript(props.tawktoScriptInputs)
        useHeadwayWidget().injectScript()
    })
</script>
